import React, { useMemo, useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from 'recharts'
import { Text } from '../../components/ui'
import type {
  MarketingRequest,
  MarketingRequestItemType,
  MarketingRequestStage,
} from '../../types/marketingSolicitacao'
import {
  ITEM_TYPE_LABELS,
  ITEM_TYPE_ORDER,
  isRequestClosed,
  tabButtonClass,
} from './solicitacaoUi'

type DashTab = 'indicators' | 'prazo'

type IndicatorChartTab =
  | 'etapa'
  | 'tipo'
  | 'solicitante'
  | 'departamento_solicitante'
  | 'responsavel'

type PrazoStatus =
  | 'no_deadline'
  | 'met'
  | 'breached'
  | 'pending_ok'
  | 'pending_breached'

const INDICATOR_CHART_TABS: { id: IndicatorChartTab; label: string }[] = [
  { id: 'etapa', label: 'Etapa' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'solicitante', label: 'Solicitante' },
  { id: 'departamento_solicitante', label: 'Dpto solicitante' },
  { id: 'responsavel', label: 'Responsável' },
]

const PRAZO_STATUS_LABELS: Record<PrazoStatus, string> = {
  no_deadline: 'Sem prazo',
  met: 'No prazo',
  breached: 'Fora do prazo',
  pending_ok: 'Pendente (no prazo)',
  pending_breached: 'Pendente (vencido)',
}

const PRAZO_STATUS_COLORS: Record<PrazoStatus, string> = {
  no_deadline: '#94a3b8',
  met: '#16a34a',
  breached: '#dc2626',
  pending_ok: '#008B95',
  pending_breached: '#ea580c',
}

const ITEM_TYPE_STACK_COLORS: Record<MarketingRequestItemType, string> = {
  brinde: '#008B95',
  peca_grafica: '#8b5cf6',
  evento: '#f97316',
  material_impresso: '#22c55e',
  comunicacao: '#eab308',
  outros: '#64748b',
}

const PIE_COLORS = ['#008B95', '#22c55e', '#eab308', '#f97316', '#a855f7', '#64748b']

const INDICATOR_CATEGORY_AXIS_WIDTH = 220
const INDICATOR_VERTICAL_BAR_MARGIN = { left: 24, right: 24, top: 8, bottom: 8 }

function monthKey(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function last6MonthKeys(): string[] {
  const keys: string[] = []
  const now = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return keys
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('pt-BR', {
    month: 'short',
    year: '2-digit',
  })
}

function truncateChartLabel(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen - 1)}…`
}

function prazoCategoryLabel(
  label: unknown,
  payload?: ReadonlyArray<{ payload?: { fullName?: string } }>
): string {
  const fullName = payload?.[0]?.payload?.fullName
  if (fullName) return fullName
  return typeof label === 'string' ? label : ''
}

function indicatorChartHeight(itemCount: number): number {
  return Math.max(256, itemCount * 36)
}

function dateOnlyKey(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10)) && value.length <= 10) {
    return value.slice(0, 10)
  }
  const d = new Date(value)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function todayDateKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function calendarDaysBetween(fromKey: string, toKey: string): number {
  const [fy, fm, fd] = fromKey.split('-').map(Number)
  const [ty, tm, td] = toKey.split('-').map(Number)
  const from = Date.UTC(fy, fm - 1, fd)
  const to = Date.UTC(ty, tm - 1, td)
  return Math.round((to - from) / 86_400_000)
}

function distinctItemTypes(request: MarketingRequest): MarketingRequestItemType[] {
  const seen = new Set<MarketingRequestItemType>()
  for (const item of request.items || []) {
    if (ITEM_TYPE_ORDER.includes(item.item_type)) seen.add(item.item_type)
  }
  return ITEM_TYPE_ORDER.filter((t) => seen.has(t))
}

function classifyPrazo(
  request: MarketingRequest,
  stages: MarketingRequestStage[],
  today: string
): PrazoStatus {
  if (!request.needed_on) return 'no_deadline'
  const needed = dateOnlyKey(request.needed_on)
  const closed = isRequestClosed(request, stages)

  if (closed) {
    const closedAt = request.closed_at || request.updated_at
    const closedKey = dateOnlyKey(closedAt)
    return closedKey <= needed ? 'met' : 'breached'
  }

  return today <= needed ? 'pending_ok' : 'pending_breached'
}

function topCountEntries(map: Map<string, number>, limit = 10): { name: string; count: number }[] {
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

const indicatorChartTabClass = (active: boolean) =>
  `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active
      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
  }`

const EmptyChart: React.FC = () => (
  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
)

const CountBarChart: React.FC<{
  data: { name: string; count: number }[]
  fill?: string
  vertical?: boolean
  name?: string
}> = ({ data, fill = '#008B95', vertical = true, name = 'Solicitações' }) => {
  if (data.length === 0) return <EmptyChart />
  if (vertical) {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={data} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
          <YAxis
            type="category"
            dataKey="name"
            width={INDICATOR_CATEGORY_AXIS_WIDTH}
            tick={{ fontSize: 11 }}
          />
          <Tooltip />
          <Bar dataKey="count" name={name} fill={fill} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ left: 8, right: 16, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={72} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip />
        <Bar dataKey="count" name={name} fill={fill} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

const IndicatorsTab: React.FC<{
  requests: MarketingRequest[]
  stages: MarketingRequestStage[]
}> = ({ requests, stages }) => {
  const [chartTab, setChartTab] = useState<IndicatorChartTab>('etapa')

  const total = requests.length
  const closed = requests.filter((r) => isRequestClosed(r, stages)).length
  const open = total - closed
  const withoutHandler = requests.filter((r) => !r.handler_id).length

  const avgResolutionDays = useMemo(() => {
    const resolved = requests.filter((r) => r.closed_at)
    if (resolved.length === 0) return null
    const totalMs = resolved.reduce((acc, r) => {
      return acc + (new Date(r.closed_at!).getTime() - new Date(r.created_at).getTime())
    }, 0)
    return Math.round(totalMs / resolved.length / 86_400_000)
  }, [requests])

  const byStageChart = useMemo(() => {
    const map: Record<string, number> = {}
    stages.forEach((s) => {
      map[s.id] = 0
    })
    requests.forEach((r) => {
      map[r.stage_id] = (map[r.stage_id] || 0) + 1
    })
    return stages.map((s) => ({ name: s.name, count: map[s.id] || 0 }))
  }, [requests, stages])

  const byTypeChart = useMemo(() => {
    const map = new Map<string, number>()
    requests.forEach((r) => {
      distinctItemTypes(r).forEach((type) => {
        const label = ITEM_TYPE_LABELS[type]
        map.set(label, (map.get(label) || 0) + 1)
      })
    })
    return ITEM_TYPE_ORDER.map((type) => ({
      name: ITEM_TYPE_LABELS[type],
      count: map.get(ITEM_TYPE_LABELS[type]) || 0,
    })).filter((x) => x.count > 0)
  }, [requests])

  const byRequesterChart = useMemo(() => {
    const map = new Map<string, number>()
    requests.forEach((r) => {
      const label = (r.requester?.name || '').trim() || 'Sem solicitante'
      map.set(label, (map.get(label) || 0) + 1)
    })
    return topCountEntries(map)
  }, [requests])

  const byRequesterDepartmentChart = useMemo(() => {
    const map = new Map<string, number>()
    requests.forEach((r) => {
      const label =
        r.requester?.department?.display_name ||
        r.requester?.department?.name ||
        'Sem departamento'
      map.set(label, (map.get(label) || 0) + 1)
    })
    return topCountEntries(map)
  }, [requests])

  const byHandlerChart = useMemo(() => {
    const map = new Map<string, number>()
    requests.forEach((r) => {
      const label = (r.handler?.name || '').trim() || 'Sem responsável'
      map.set(label, (map.get(label) || 0) + 1)
    })
    return topCountEntries(map)
  }, [requests])

  const openVsClosedPie = useMemo(() => {
    return [
      { name: 'Em aberto', value: open, fill: '#008B95' },
      { name: 'Encerrados', value: closed, fill: '#22c55e' },
    ].filter((x) => x.value > 0)
  }, [open, closed])

  const stageTypeStack = useMemo(() => {
    return stages.map((s) => {
      const list = requests.filter((r) => r.stage_id === s.id)
      const row: Record<string, string | number> = { name: s.name }
      ITEM_TYPE_ORDER.forEach((type) => {
        row[ITEM_TYPE_LABELS[type]] = list.filter((r) =>
          distinctItemTypes(r).includes(type)
        ).length
      })
      return row
    })
  }, [requests, stages])

  const monthlyChart = useMemo(() => {
    const keys = last6MonthKeys()
    const novos: Record<string, number> = {}
    const closedMap: Record<string, number> = {}
    keys.forEach((k) => {
      novos[k] = 0
      closedMap[k] = 0
    })

    requests.forEach((r) => {
      const mk = monthKey(r.created_at)
      if (novos[mk] !== undefined) novos[mk]++
      if (r.closed_at) {
        const ck = monthKey(r.closed_at)
        if (closedMap[ck] !== undefined) closedMap[ck]++
      }
    })

    let cumNovos = 0
    return keys.map((k) => {
      cumNovos += novos[k]
      return {
        name: monthLabel(k),
        Novos: novos[k],
        Encerrados: closedMap[k],
        acumNovos: cumNovos,
      }
    })
  }, [requests])

  const activeChartData =
    chartTab === 'etapa'
      ? byStageChart
      : chartTab === 'tipo'
        ? byTypeChart
        : chartTab === 'solicitante'
          ? byRequesterChart
          : chartTab === 'departamento_solicitante'
            ? byRequesterDepartmentChart
            : byHandlerChart

  const activeChartMeta: Record<
    IndicatorChartTab,
    { title: string; subtitle: string; fill: string; vertical: boolean }
  > = {
    etapa: {
      title: 'Etapa',
      subtitle: 'Volume por coluna do fluxo',
      fill: '#8b5cf6',
      vertical: false,
    },
    tipo: {
      title: 'Tipo',
      subtitle: 'Solicitações por tipo de item (uma contagem por tipo distinto)',
      fill: '#008B95',
      vertical: false,
    },
    solicitante: {
      title: 'Solicitante',
      subtitle: 'Até 10 solicitantes com mais pedidos',
      fill: '#06b6d4',
      vertical: true,
    },
    departamento_solicitante: {
      title: 'Departamento solicitante',
      subtitle: 'Até 10 departamentos com mais pedidos',
      fill: '#f97316',
      vertical: true,
    },
    responsavel: {
      title: 'Responsável',
      subtitle: 'Até 10 responsáveis com mais pedidos',
      fill: '#22c55e',
      vertical: true,
    },
  }

  const meta = activeChartMeta[chartTab]

  return (
    <>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Total de solicitações</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">{total}</div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Em aberto</div>
          <div className="mt-1 text-3xl font-semibold text-primary-600 dark:text-primary-400">{open}</div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Encerrados</div>
          <div className="mt-1 text-3xl font-semibold text-green-600 dark:text-green-400">{closed}</div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Sem responsável</div>
          <div className="mt-1 text-3xl font-semibold text-orange-600 dark:text-orange-400">
            {withoutHandler}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Tempo médio de atendimento</div>
          <div className="mt-1 text-3xl font-semibold text-purple-600 dark:text-purple-400">
            {avgResolutionDays !== null ? `${avgResolutionDays}d` : '—'}
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="overflow-x-auto border-b border-gray-200 px-4 dark:border-gray-700">
          <nav className="-mb-px flex min-w-max gap-x-4">
            {INDICATOR_CHART_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={indicatorChartTabClass(chartTab === tab.id)}
                onClick={() => setChartTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-5">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            {meta.title}
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            {meta.subtitle}
          </Text>
          <div
            style={{
              height: meta.vertical ? indicatorChartHeight(activeChartData.length) : 256,
            }}
          >
            <CountBarChart
              data={activeChartData}
              fill={meta.fill}
              vertical={meta.vertical}
            />
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Aberto vs encerrado
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Visão geral do portfólio filtrado
          </Text>
          <div className="flex h-64 items-center justify-center">
            {openVsClosedPie.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={openVsClosedPie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {openVsClosedPie.map((entry, index) => (
                      <Cell key={entry.name} fill={entry.fill || PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Tipo por etapa
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Empilhado — tipos de item em cada coluna do fluxo
          </Text>
          <div style={{ height: indicatorChartHeight(stageTypeStack.length) }}>
            {stageTypeStack.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={stageTypeStack} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={INDICATOR_CATEGORY_AXIS_WIDTH}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip />
                  <Legend />
                  {ITEM_TYPE_ORDER.map((type, index) => (
                    <Bar
                      key={type}
                      dataKey={ITEM_TYPE_LABELS[type]}
                      stackId="s"
                      fill={ITEM_TYPE_STACK_COLORS[type]}
                      radius={index === ITEM_TYPE_ORDER.length - 1 ? [0, 4, 4, 0] : undefined}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
          Evolução mensal (últimos 6 meses)
        </Text>
        <Text size="xs" color="muted" className="mb-3">
          Barras: novos registros (data de criação) e encerramentos (data de encerramento). Linha:
          acumulado de novos.
        </Text>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="Novos"
                name="Novos no mês"
                fill="#008B95"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="left"
                dataKey="Encerrados"
                name="Encerrados no mês"
                fill="#22c55e"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="acumNovos"
                name="Acumulado novos"
                stroke="#a855f7"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )
}

const PrazoTab: React.FC<{
  requests: MarketingRequest[]
  stages: MarketingRequestStage[]
}> = ({ requests, stages }) => {
  const today = todayDateKey()

  const classified = useMemo(() => {
    return requests.map((request) => ({
      request,
      status: classifyPrazo(request, stages, today),
    }))
  }, [requests, stages, today])

  const {
    withDeadlineCount,
    met,
    breached,
    pendingOk,
    pendingBreached,
    avgDelayDays,
  } = useMemo(() => {
    const withDeadline = classified.filter((c) => c.status !== 'no_deadline')
    const metCount = classified.filter((c) => c.status === 'met').length
    const breachedCount = classified.filter((c) => c.status === 'breached').length
    const pendingOkCount = classified.filter((c) => c.status === 'pending_ok').length
    const pendingBreachedCount = classified.filter((c) => c.status === 'pending_breached').length

    const breachedClosed = classified.filter((c) => c.status === 'breached')
    let avg: number | null = null
    if (breachedClosed.length > 0) {
      const totalDays = breachedClosed.reduce((acc, { request }) => {
        if (!request.needed_on) return acc
        const closedAt = request.closed_at || request.updated_at
        return acc + calendarDaysBetween(dateOnlyKey(request.needed_on), dateOnlyKey(closedAt))
      }, 0)
      avg = Math.round(totalDays / breachedClosed.length)
    }

    return {
      withDeadlineCount: withDeadline.length,
      met: metCount,
      breached: breachedCount,
      pendingOk: pendingOkCount,
      pendingBreached: pendingBreachedCount,
      avgDelayDays: avg,
    }
  }, [classified])

  const inDayPct =
    withDeadlineCount > 0
      ? Math.round(((met + pendingOk) / withDeadlineCount) * 100)
      : null
  const closedWithDeadline = met + breached
  const closedOnTimePct =
    closedWithDeadline > 0 ? Math.round((met / closedWithDeadline) * 100) : null

  const pieData = useMemo(() => {
    const counts: Record<PrazoStatus, number> = {
      no_deadline: 0,
      met: 0,
      breached: 0,
      pending_ok: 0,
      pending_breached: 0,
    }
    classified.forEach((c) => {
      counts[c.status]++
    })
    return (Object.keys(counts) as PrazoStatus[])
      .filter((k) => counts[k] > 0)
      .map((k) => ({ name: PRAZO_STATUS_LABELS[k], value: counts[k], fill: PRAZO_STATUS_COLORS[k] }))
  }, [classified])

  const byTypeData = useMemo(() => {
    const typeMap: Record<string, Record<PrazoStatus, number>> = {}
    classified.forEach(({ request, status }) => {
      const types = distinctItemTypes(request)
      const labels = types.length > 0 ? types.map((t) => ITEM_TYPE_LABELS[t]) : ['Sem tipo']
      labels.forEach((label) => {
        if (!typeMap[label]) {
          typeMap[label] = {
            no_deadline: 0,
            met: 0,
            breached: 0,
            pending_ok: 0,
            pending_breached: 0,
          }
        }
        typeMap[label][status]++
      })
    })
    return Object.entries(typeMap).map(([name, counts]) => ({
      name,
      'No prazo': counts.met,
      'Fora do prazo': counts.breached,
      'Pend. no prazo': counts.pending_ok,
      'Pend. vencido': counts.pending_breached,
      'Sem prazo': counts.no_deadline,
    }))
  }, [classified])

  const byDeptData = useMemo(() => {
    const map = new Map<
      string,
      { met: number; breached: number; pending_ok: number; pending_breached: number; total: number; fullName: string }
    >()
    classified
      .filter((c) => c.status !== 'no_deadline')
      .forEach(({ request, status }) => {
        const key =
          request.requester?.department?.display_name ||
          request.requester?.department?.name ||
          'Sem departamento'
        const prev =
          map.get(key) ?? {
            met: 0,
            breached: 0,
            pending_ok: 0,
            pending_breached: 0,
            total: 0,
            fullName: key,
          }
        prev[status as 'met' | 'breached' | 'pending_ok' | 'pending_breached']++
        prev.total++
        map.set(key, prev)
      })
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((d) => ({
        name: truncateChartLabel(d.fullName, 22),
        fullName: d.fullName,
        'No prazo': d.met,
        'Fora do prazo': d.breached,
        'Pend. no prazo': d.pending_ok,
        'Pend. vencido': d.pending_breached,
      }))
  }, [classified])

  const byHandlerData = useMemo(() => {
    const map = new Map<
      string,
      { met: number; breached: number; pending_ok: number; pending_breached: number; total: number; fullName: string }
    >()
    classified
      .filter((c) => c.status !== 'no_deadline')
      .forEach(({ request, status }) => {
        const key = (request.handler?.name || '').trim() || 'Sem responsável'
        const prev =
          map.get(key) ?? {
            met: 0,
            breached: 0,
            pending_ok: 0,
            pending_breached: 0,
            total: 0,
            fullName: key,
          }
        prev[status as 'met' | 'breached' | 'pending_ok' | 'pending_breached']++
        prev.total++
        map.set(key, prev)
      })
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map((d) => ({
        name: truncateChartLabel(d.fullName, 22),
        fullName: d.fullName,
        'No prazo': d.met,
        'Fora do prazo': d.breached,
        'Pend. no prazo': d.pending_ok,
        'Pend. vencido': d.pending_breached,
      }))
  }, [classified])

  const monthlyData = useMemo(() => {
    const keys = last6MonthKeys()
    type Bucket = { withDeadline: number; breached: number; met: number }
    const buckets: Record<string, Bucket> = {}
    keys.forEach((k) => {
      buckets[k] = { withDeadline: 0, breached: 0, met: 0 }
    })

    classified.forEach(({ request, status }) => {
      const mk = monthKey(request.created_at)
      if (!buckets[mk] || status === 'no_deadline') return
      buckets[mk].withDeadline++
      if (status === 'met') buckets[mk].met++
      if (status === 'breached') buckets[mk].breached++
    })

    return keys.map((k) => ({
      name: monthLabel(k),
      'Com prazo': buckets[k].withDeadline,
      'No prazo': buckets[k].met,
      'Fora do prazo': buckets[k].breached,
    }))
  }, [classified])

  return (
    <>
      <div className="mb-3 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Com prazo (amostra)</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
            {withDeadlineCount}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Em dia (operacional)</div>
          <div className="mt-1 text-3xl font-semibold text-green-600 dark:text-green-400">
            {inDayPct !== null ? `${inDayPct}%` : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Concluídas no prazo</div>
          <div className="mt-1 text-3xl font-semibold text-primary-600 dark:text-primary-400">
            {closedOnTimePct !== null ? `${closedOnTimePct}%` : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Pendentes vencidas</div>
          <div className="mt-1 text-3xl font-semibold text-orange-600 dark:text-orange-400">
            {pendingBreached}
          </div>
        </div>
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="text-xs text-gray-500 dark:text-gray-400">Atraso médio (fora do prazo)</div>
          <div className="mt-1 text-3xl font-semibold text-purple-600 dark:text-purple-400">
            {avgDelayDays !== null ? `${avgDelayDays}d` : '—'}
          </div>
        </div>
      </div>
      <p className="mb-6 text-xs text-gray-400 dark:text-gray-500">
        Prazo pela data <strong>Para quando</strong>, em dias de calendário. &quot;Em dia&quot; inclui
        concluídas no prazo e pendentes ainda no prazo; &quot;Concluídas no prazo&quot; considera só as
        finalizadas com data definida.
      </p>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Distribuição por status do prazo
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Visão geral do portfólio filtrado
          </Text>
          <div className="flex h-64 items-center justify-center">
            {pieData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={80}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [Number(v), name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
            Prazo por tipo de item
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Comparativo de status por tipo (uma contagem por tipo distinto)
          </Text>
          <div className="h-64">
            {byTypeData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byTypeData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={64} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="No prazo" stackId="s" fill="#16a34a" />
                  <Bar dataKey="Fora do prazo" stackId="s" fill="#dc2626" />
                  <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                  <Bar dataKey="Pend. vencido" stackId="s" fill="#ea580c" />
                  <Bar dataKey="Sem prazo" stackId="s" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
          Prazo por departamento solicitante (top 10)
        </Text>
        <Text size="xs" color="muted" className="mb-3">
          Solicitações com prazo — distribuição de status por departamento
        </Text>
        <div className="h-72">
          {byDeptData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={byDeptData} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={prazoCategoryLabel} />
                <Legend />
                <Bar dataKey="No prazo" stackId="s" fill="#16a34a" />
                <Bar dataKey="Fora do prazo" stackId="s" fill="#dc2626" />
                <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                <Bar dataKey="Pend. vencido" stackId="s" fill="#ea580c" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
          Prazo por responsável (top 10)
        </Text>
        <Text size="xs" color="muted" className="mb-3">
          Solicitações com prazo — distribuição de status por responsável
        </Text>
        <div className="h-72">
          {byHandlerData.length === 0 ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={byHandlerData} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={prazoCategoryLabel} />
                <Legend />
                <Bar dataKey="No prazo" stackId="s" fill="#16a34a" />
                <Bar dataKey="Fora do prazo" stackId="s" fill="#dc2626" />
                <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                <Bar dataKey="Pend. vencido" stackId="s" fill="#ea580c" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <Text size="sm" className="mb-1 font-medium text-gray-700 dark:text-gray-300">
          Evolução mensal — prazo (últimos 6 meses)
        </Text>
        <Text size="xs" color="muted" className="mb-3">
          Por mês de abertura. &quot;Com prazo&quot; = total com data Para quando; &quot;No prazo&quot; e
          &quot;Fora do prazo&quot; = apenas concluídas.
        </Text>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Com prazo" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              <Bar dataKey="No prazo" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Fora do prazo" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )
}

export interface SolicitacaoDashboardProps {
  requests: MarketingRequest[]
  stages: MarketingRequestStage[]
}

const SolicitacaoDashboard: React.FC<SolicitacaoDashboardProps> = ({ requests, stages }) => {
  const [activeTab, setActiveTab] = useState<DashTab>('indicators')

  return (
    <div>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Dashboard">
          <button
            type="button"
            className={tabButtonClass(activeTab === 'indicators')}
            onClick={() => setActiveTab('indicators')}
          >
            Indicadores
          </button>
          <button
            type="button"
            className={tabButtonClass(activeTab === 'prazo')}
            onClick={() => setActiveTab('prazo')}
          >
            Prazo
          </button>
        </nav>
      </div>

      {activeTab === 'indicators' ? (
        <IndicatorsTab requests={requests} stages={stages} />
      ) : (
        <PrazoTab requests={requests} stages={stages} />
      )}
    </div>
  )
}

export default SolicitacaoDashboard
